import { useEffect, useState } from "react";
import "../styles/myplan.css";
import moment from "moment";
import { Card, CardBody, CardHeader, Table } from "reactstrap";
import Loader from "./Common/Loader";
import { DataPointTypes } from "../types";
import { NoResult } from "./Common/NoResult";
import { useSelector } from "react-redux";
import { fetchUSNPlanRank, fetchUSNPlanRankDetail } from "../api/usnPlan";
import Datepicker from "./Datepicker";
import { selectUtility } from "../store/utility/selector";
import { RankChart } from "./Charts/RankChart";
import { Typography } from "@mui/material";

interface PlanRankDetail {
  base_rate: string;
  date: string;
  pd_id: string;
  plan_id: string;
  rank: number;
}

interface ChartSeries {
  [key: string]: DataPointTypes;
}

type PlanRank = any;

interface GroupedPlans {
  title: string;
  plans: PlanRank[];
}


const USN_MyPlanRank = () => {
  const currentLocation = useSelector<
    any,
    { state_id: string; state_name: string }
  >((state) => state.Location.currentLocation);
  const [isLoading, setIsLoading] = useState(false);
  const [rankTableData, setRankTableData] = useState<PlanRank>([]);
  const [termTableData, setTermTableData] = useState<Array<GroupedPlans>>([])
  const [filterValue, setFilterValue] = useState({
    created_ts: moment().format("YYYY-MM-DD"),
  });
  const [chartSeries, setChartSeries] = useState<ChartSeries>({});
  const currentUtility = useSelector(selectUtility);

  useEffect(() => {
    const getPlanRank = async () => {
      setIsLoading(true);

      fetchUSNPlanRank(
        currentLocation?.state_id,
        currentUtility.utility_id,
        filterValue.created_ts,
        "1"
      ).then((res) => {
        setIsLoading(false);
        if (res && res.status === 1) {
          const data = sortData(res.response)
          setRankTableData(res.response);
          setTermTableData(data);
        }
        else {
          setRankTableData([]);
          setTermTableData([]);
        }
      });
    };

    if (currentUtility) {
      getPlanRank();
    }
  }, [filterValue, currentUtility]);

  useEffect(() => {
    if (rankTableData.length === 0) return;

    for (let item of rankTableData) {
      getRankData(item.term);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankTableData]);

  const getRankData = (term: string) => {
    fetchUSNPlanRankDetail(
      currentLocation?.state_id,
      filterValue.created_ts,
      currentUtility.utility_id,
      term,
      "1"
    ).then((res) => {
      let each: DataPointTypes[] = [];
      if (res && res.status === 1) {
        const { response } = res;
        response.forEach((data: PlanRankDetail) => {
          if (
            data.rank !== 0 &&
            moment(data.date, "MM/DD/YYYY").isAfter(
              moment(filterValue.created_ts, "YYYY-MM-DD").subtract(1, "month")
            )
          ) {
            each.push({
              x: data.date,
              y: data.rank,
              detail: data,
            });
          }
        });
      }
      setChartSeries((prevState: any) => ({ ...prevState, [term]: each }));
    });
  };

  const sortData = (plans:PlanRank[]) => {
    const numericPlans = plans.map(plan => ({
      ...plan,
      termNum: parseInt(plan.term, 10)
      }));

      const longTermPlans = numericPlans.filter(plan => plan.termNum >= 12);
      const shortTermPlans = numericPlans.filter(plan => plan.termNum < 12);

      longTermPlans.sort((a, b) => a.termNum - b.termNum); 
      shortTermPlans.sort((a, b) => b.termNum - a.termNum);

      const formatPlans = (plans: Array<PlanRank & { termNum: number }>) => 
          plans.map(({ termNum, ...rest }) => rest);

      return [
          {
              title: "Term ≥ 12",
              plans: formatPlans(longTermPlans)
          },
          {
              title: "Term < 12",
              plans: formatPlans(shortTermPlans)
          }
      ];
  }

  const changeDate = (val: any) => {
    setFilterValue(
      Object.assign({}, filterValue, {
        created_ts: moment(val).format("YYYY-MM-DD"),
      })
    );
  };

  const displayRaw = () => {
    if (isLoading && rankTableData.length === 0) return <Loader />;
    if (rankTableData.length > 0 && termTableData.length > 0) {
      return (
        <>
          {termTableData.map((group: any) => (
            <div key={group.title} className="plan-group">
              <h3 className="group-title" style={{fontSize:'16px', color:"#878A99"}}>{group.title}</h3>
              {group.plans
                .filter((plan: any) => !["48", "60"].includes(plan.term))
                .map((plan: any) => (
                  <Row
                    key={`${group.title}-${plan.term}-${plan.plan_id}`} 
                    data={plan}
                    chartData={ chartSeries[plan.term] ? chartSeries[plan.term] : ([] as any)}
                    isLoading={isLoading}
                    handleChangeSource={(term: string) => {
                      getRankData(term);
                    }}
                  />
                ))}
            </div>
          ))}
        </>
      );
    }
    return <NoResult />;
  };

  return (
    <Card>
      <CardHeader>
        <h4 className="card-title mb-0 flex-grow-1">Ranking on Select Shopping Sites</h4>
        {currentLocation && currentUtility && (
          <Typography
            variant="subtitle2"
            className="text-muted"
          >{`${currentLocation.state_name} - ${currentUtility.utility_name}`}</Typography>
        )}
      </CardHeader>
      <CardBody>
        <div className="d-flex align-items-start justify-content-end gap-3 mb-3">
          <Datepicker
            handleChangeDate={(val: any) => changeDate(val)}
            disabled={isLoading}
          />
        </div>
        {displayRaw()}
      </CardBody>
    </Card>
  );
};

const Row = ({
  data,
  isLoading,
  chartData,
  handleChangeSource,
}: {
  data: any;
  isLoading: boolean;
  chartData: DataPointTypes[];
  handleChangeSource: Function;
}) => {
  const [selectedSource, setSelectedSource] = useState("market");

  const { term, source } = data;

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between fs-16">
        <div>Term: {term}</div>
      </CardHeader>
      <CardBody className="row">
        <div className="col-lg-7 col-12">
          <RankChart
            title={`Rank Trend by ${selectedSource.toLocaleUpperCase()}`}
            data={
              chartData?.length > 0
                ? [{ id: `Term ${term}`, data: chartData }]
                : []
            }
            isLoading={isLoading}
          />
        </div>
        <div className="col-lg-5 col-12 rank_trend_market_table">
          <Table className="table-borderless">
            <thead>
              <tr>
                <th></th>
                <th>Source</th>
                <th>Lowest Rate</th>
                <th className="text-end">Rank</th>
              </tr>
            </thead>
            <tbody>
              {source &&
                Object.keys(source).map((idx) => {
                  if (!source[idx].rate) return null;
                  return (
                    <tr
                      key={idx}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedSource === idx ? "#f7f7f7" : "transparent",
                      }}
                      onClick={() => {
                        if (selectedSource === idx) return;
                        setSelectedSource(idx);
                        handleChangeSource(
                          term,
                          idx === "market" ? "" : source[idx].source
                        );
                      }}
                    >
                      <td className="pe-0">
                        {selectedSource === idx && (
                          <i className="fs-20 me-2 bx bx-chevrons-right text-secondary" />
                        )}
                      </td>
                      <td>
                        <span className="text-uppercase">{idx}</span>
                      </td>
                      <td>
                        <span>{source[idx].plan_name}</span>
                        <br />
                        <span>
                          <b>{source[idx].rate + "¢"}</b>
                          {/* {source[idx].plan_id && (
                            <Button
                              onClick={() =>
                                jumpTo(source[idx].plan_id, source[idx].pd_id)
                              }
                              outline
                              rel="noreferrer"
                              className="btn btn-soft-secondary btn-sm ms-2"
                            >
                              EFL
                            </Button>
                          )} */}
                        </span>
                      </td>
                      <td className="text-end">
                        <b>{source[idx].rank}</b>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default USN_MyPlanRank;
